import { Expose, Transform, Type } from 'class-transformer';
import { dayjsTransformer } from '@hidat/huijs-dates';
import * as dayjs from 'dayjs';
import { AnyHash, Uuid } from '@hidat/huijs-common';
import {
  ContentToPlayInterface,
  ItemToPlayInterface,
  MediaToPlayInterface,
  PlaybackInfoInterface,
  SegmentToPlayInterface,
  ShowToPlayInterface,
} from '@splixer/common';
import { PlayerSegment } from './player-segment.model';
import { PlayerShow } from './show.model';
import { PlayerHost } from './host.model';

export class PlaybackInfo implements PlaybackInfoInterface {
  mediaItemId: number | undefined;
  @Expose()
  duration: number | undefined;
  expiresAt: dayjs.Dayjs | undefined;
  @Expose()
  @Type(() => String)
  url: string[] | undefined;
}

/**
 * Content Item
 * The details/metadata about a piece of content.
 * This is for display purposes.
 */
export class PlayerContentItem {
  @Expose({ name: 'name' })
  name: string | undefined = undefined;
  @Expose({ name: 'performer_name' })
  performerName: string | undefined = undefined;
  @Expose({ name: 'release_name' })
  releaseName: string | undefined = undefined;
  @Expose({ name: 'label_name' })
  labelName: string | undefined = undefined;
  @Expose({ name: 'release_date' })
  releaseDate: string | undefined = undefined;
  @Expose({ name: 'cover_art_url' })
  coverArtUrl: string | undefined = undefined;
  @Expose({ name: 'comment' })
  comment: string | undefined = undefined;
  @Expose({ name: 'content_type' })
  contentType: string | undefined = undefined;
  @Expose({ name: 'default' })
  default = false;

  // When was the item originally broadcasted at
  @Expose({ name: 'broadcasted_at' })
  @Transform(dayjsTransformer, { toClassOnly: true })
  broadcastedAt: dayjs.Dayjs | undefined = undefined;
}

export class ContentToPlay implements ContentToPlayInterface {
  @Expose({ toClassOnly: true })
  uid: Uuid = 'not-a-guid';
  @Expose()
  seqNum = 0;
  @Expose()
  @Transform(dayjsTransformer, { toClassOnly: true })
  scheduledAt: unknown | undefined;
  @Expose()
  audioOffset = 0;
  @Expose()
  @Type(() => PlayerContentItem)
  content: PlayerContentItem | undefined = undefined;

  // Non-persisted items
  startedAt: dayjs.Dayjs | undefined;
  finishedAt: dayjs.Dayjs | undefined;
}

export abstract class PlayerItemToPlay implements ItemToPlayInterface {
  @Expose({ toClassOnly: true })
  uid: Uuid = 'not-a-guid';
  @Expose()
  @Type(() => PlayerHost)
  _host: PlayerHost | undefined;
  @Expose()
  lastItem = false;

  abstract getShow(): PlayerShow | undefined;
  abstract getMediaToPlay(): MediaToPlay[];
}

export class MediaToPlay
  extends PlayerItemToPlay
  implements MediaToPlayInterface
{
  @Expose()
  seqNum = 0;
  @Expose()
  duration = 0;
  @Expose()
  @Transform(dayjsTransformer, { toClassOnly: true })
  scheduledAt: unknown | undefined;
  @Expose()
  @Type(() => PlaybackInfo)
  playback: PlaybackInfo | undefined;
  @Expose()
  @Type(() => ContentToPlay)
  contents: ContentToPlay[] | undefined;
  @Expose()
  @Type(() => PlayerSegment)
  _segment: PlayerSegment | undefined;
  @Expose()
  @Type(() => PlayerShow)
  _show: PlayerShow | undefined;

  // Non-persisted
  currentContentItem: ContentToPlay | undefined;
  currentContent: AnyHash | undefined;
  currentContentStart = 0;
  currentContentEnd = 0;

  schedulingItem: PlayerItemToPlay | undefined;
  firstInItem = false;
  lastInItem = false;

  override getShow(): PlayerShow | undefined {
    return this._show;
  }

  override getMediaToPlay(): MediaToPlay[] {
    return [this];
  }

  getContentPlayingAt(
    position: number,
    assignCurrentContent = true,
  ): ContentToPlay | undefined {
    if (!position || !this.contents) {
      return undefined;
    }

    if (
      position >= this.currentContentEnd ||
      position < this.currentContentStart
    ) {
      let bestCtp: ContentToPlay | undefined;
      let endsAt: number | undefined;
      for (const ctp of this.contents) {
        if (ctp.audioOffset != null && ctp.audioOffset > position) {
          endsAt = ctp.audioOffset;
          break;
        } else {
          bestCtp = ctp;
        }
      }

      if (bestCtp && assignCurrentContent) {
        this.currentContentItem = bestCtp;
        this.currentContent = bestCtp.content;
        this.currentContentStart = bestCtp.audioOffset ?? 0;
        this.currentContentEnd =
          endsAt ?? this.playback?.duration ?? this.currentContentStart;
      }

      return bestCtp;
    }
    return this.currentContentItem;
  }
}

export class SegmentToPlay
  extends PlayerItemToPlay
  implements SegmentToPlayInterface
{
  @Expose()
  @Type(() => PlayerSegment)
  segment!: PlayerSegment;
  @Expose()
  @Type(() => MediaToPlay)
  mediaToPlay: MediaToPlay[] = [];
  @Expose()
  @Type(() => PlayerShow)
  _show?: PlayerShow;

  override getShow(): PlayerShow | undefined {
    return this._show;
  }

  override getMediaToPlay(): MediaToPlay[] {
    return this.mediaToPlay;
  }
}

export class ShowToPlay
  extends PlayerItemToPlay
  implements ShowToPlayInterface
{
  @Expose()
  @Type(() => PlayerShow)
  show!: PlayerShow;
  @Expose()
  @Type(() => SegmentToPlay)
  segmentsToPlay: SegmentToPlay[] = [];

  override getShow(): PlayerShow | undefined {
    return this.show;
  }

  override getMediaToPlay(): MediaToPlay[] {
    const medias: MediaToPlay[] = [];
    for (const stp of this.segmentsToPlay) {
      medias.push(...stp.getMediaToPlay());
    }
    return medias;
  }
}
